/// Poll'er `/api/system/status` periodisk og opdaterer status-pillen i
/// topbaren med tone (ok/warn/err) + label. Bruger Page Visibility API til
/// at pause polling når fanen er skjult — sparer probe-trafik mod
/// SmtpListener + ip_reputation-DB-roundtrip når ingen kigger.
///
/// Pillen markeres i HTML via `data-system-pill` + `data-system-pill-label`
/// så vi ikke har nogen kobling mod ID'er der nemt rives ud ved layout-rewrites.

type Tone = 'ok' | 'warn' | 'err';

interface SystemStatusResponse {
  tone: Tone;
  label: string;
  apiUp: boolean;
  smtpUp: boolean;
  outboundPending: number;
}

export class SystemStatusPoller {
  private static readonly ENDPOINT = '/api/system/status';
  private static readonly INTERVAL_MS = 30_000;

  private timer: number | null = null;

  constructor(private readonly doc: Document = document) {}

  start(): void {
    if (!this.findPill()) return; // ingen pill på siden — typisk login-flow
    void this.pollOnce();
    this.scheduleNext();
    this.doc.addEventListener('visibilitychange', () => {
      if (this.doc.hidden) {
        this.stop();
      } else {
        void this.pollOnce();
        this.scheduleNext();
      }
    });
  }

  stop(): void {
    if (this.timer !== null) {
      window.clearInterval(this.timer);
      this.timer = null;
    }
  }

  private scheduleNext(): void {
    this.stop();
    this.timer = window.setInterval(() => void this.pollOnce(), SystemStatusPoller.INTERVAL_MS);
  }

  private async pollOnce(): Promise<void> {
    try {
      const resp = await fetch(SystemStatusPoller.ENDPOINT, {
        credentials: 'same-origin',
        headers: { 'Accept': 'application/json' },
      });
      if (!resp.ok) {
        this.applyError(resp.status === 401 ? 'session udløbet' : `endpoint ${resp.status}`);
        return;
      }
      const data = (await resp.json()) as SystemStatusResponse;
      this.applyStatus(data);
    } catch {
      this.applyError('ingen forbindelse');
    }
  }

  private applyStatus(data: SystemStatusResponse): void {
    const pill = this.findPill();
    const label = this.findLabel();
    if (!pill || !label) return;
    pill.classList.remove('status-pill-warn', 'status-pill-err');
    if (data.tone === 'warn') pill.classList.add('status-pill-warn');
    else if (data.tone === 'err') pill.classList.add('status-pill-err');
    label.textContent = data.label;
    pill.setAttribute('title', this.buildTooltip(data));
  }

  private applyError(detail: string): void {
    const pill = this.findPill();
    const label = this.findLabel();
    if (!pill || !label) return;
    pill.classList.remove('status-pill-warn');
    pill.classList.add('status-pill-err');
    label.textContent = 'Status-tjek fejlede';
    pill.setAttribute('title', detail);
  }

  private findPill(): HTMLElement | null {
    return this.doc.querySelector<HTMLElement>('[data-system-pill]');
  }

  private findLabel(): HTMLElement | null {
    return this.doc.querySelector<HTMLElement>('[data-system-pill-label]');
  }

  private buildTooltip(data: SystemStatusResponse): string {
    const parts = [
      `API: ${data.apiUp ? 'OK' : 'NEDE'}`,
      `SMTP: ${data.smtpUp ? 'OK' : 'NEDE'}`,
      `Outbound-kø: ${data.outboundPending}`,
    ];
    return parts.join(' · ');
  }
}
